import { Box, Button, Grid, IconButton, Typography, LinearProgress, Chip } from '@mui/material'
import { Company } from '@/@data/company'
import { useTranslator } from '@/hooks/useTranslator'
import dayjs, { Dayjs } from 'dayjs'
import { UploadContractDialog } from './UploadContractDialog'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

const AVERAGEE_PERCENT = 69

export const EmployerActiveContract = () => {
  const { __ } = useTranslator()

  const [openUploadContractDialog, setOpenUploadContractDialog] = useState(false)
  const { id } = useParams()

  const { data: dataContract, refetch } = useQuery({
    ...Company.getActiveContract(Number(id)),
    enabled: !!id
  })

  const today = dayjs()
  const startDate = dataContract?.data?.start_date ? dayjs(new Date(dataContract.data.start_date)) : null
  const endDate = dataContract?.data?.end_date ? dayjs(new Date(dataContract.data.end_date)) : null

  const totalDays = startDate && endDate ? endDate.diff(startDate, 'day') + 1 : 0
  const passedDays = startDate ? Math.min(today.diff(startDate, 'day'), totalDays) : 0
  const remainingDays = totalDays - passedDays
  const progressPercentage = totalDays > 0 ? Math.min(Math.max((passedDays / totalDays) * 100, 0), 100) : 0

  const getContractStatus = (end: Dayjs | null) => {
    if (end) {
      // const diff = end.diff(start, 'day') || 0
      const diffCrr = end.diff(dayjs(new Date()), 'day') || 0

      if (diffCrr < 0) {
        return 'Expired'
      }

      // const diffAverage = ((diff - diffCrr) / diff) * 100

      // if (diffAverage >= 0 && diffAverage <= AVERAGEE_PERCENT) {
      //   return 'Active'
      // } else if (diffAverage > AVERAGEE_PERCENT && diffAverage <= 100) {
      //   return 'Termination Pending'
      // }

      if (progressPercentage && progressPercentage >= 0 && progressPercentage <= AVERAGEE_PERCENT) {
        return 'Active'
      } else if (progressPercentage > AVERAGEE_PERCENT && progressPercentage <= 100) {
        return 'Termination Pending'
      }

      return 'Active'
      // if (end) {
      //   const diff = end.diff(start, 'day') || 0
      //   const diffCrr = end.diff(dayjs(new Date()),'day') || 0
      //   if (diffCrr < 0 ) {
      //     return 'Expired'
      //   }
      //   const diffAverage = ((diff - diffCrr) / diff)*100;
      //   if (diffAverage >= 0 && diffAverage <= AVERAGEE_PERCENT) {
      //     return 'Active'
      //   } else if (diffAverage > AVERAGEE_PERCENT && diffAverage <= 100) {
      //     return 'Termination Pending'
      //   }
      // }
      // return 'Active'

      // const oneMonthFromEnd = endDate.subtract(1, 'month')

      // if (today.isAfter(endDate.add(3, 'day'))) {
      //   return 'Expired'
      // } else if (today.isAfter(oneMonthFromEnd)) {
      //   return 'Termination Pending'
      // } else {
      //   return 'Active'
      // }
    }
  }

  const contractStatus = getContractStatus(endDate)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return '#28C76F'
      case 'Termination Pending':
        return '#FF9F43'
      case 'Expired':
        return '#EA5455'
      default:
        return '#6E6B7B'
    }
  }

  const getBackgroundColor = (status: string) => {
    switch (status) {
      case 'Active':
        return '#D3EBDE'
      case 'Termination Pending':
        return '#F5E5D7'
      case 'Expired':
        return '#F5D7D9'
      default:
        return '#6E6B7B'
    }
  }

  const getProgressBarColor = (percentage: number) => {
    if (percentage >= 100) {
      return '#ff4c51' // Red for 100%
    } else if (percentage >= 70) {
      return '#ff9f43' // Orange for 70-99%
    } else {
      return '#3551F2' // Blue for 0-69%
    }
  }

  const handleDownload = async () => {
    if (dataContract?.data?.file?.url) {
      try {
        const response = await fetch(dataContract.data.file.url)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')

        link.href = url
        link.download = dataContract.data.file.name || 'contract-file'
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        window.open(dataContract.data.file.url, '_blank')
      }
    }
  }

  return (
    <Box className='flex flex-col gap-6 p-6'>
      <div className='flex flex-col gap-4'>
        <Box className='flex items-center justify-between'>
          <Typography className='text-[18px] leading-[28px] font-medium'>{__('Active Contract')}</Typography>
          <Button
            variant='contained'
            className='text-[#3551F2] bg-[#E8E6FC] shadow-none rounded-full'
            startIcon={<i className='tabler-upload' />}
            onClick={() => setOpenUploadContractDialog(true)}
          >
            {__('Upload Active Contract')}
          </Button>
        </Box>
        <Typography className='text-[15px] leading-[22px] font-normal '>
          {__(
            'This dedicated section is designed for secure and efficient management of your company’s contract documents. You may upload, review, or remove contractual files as necessary, ensuring seamless document control.'
          )}
        </Typography>
      </div>
      {dataContract?.data ? (
        <Box className='p-4 bg-[#F3F2F3] rounded-[16px] gap-2 flex flex-col'>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <div className='flex flex-row gap-2'>
                <Typography className='text-[15px] leading-[22px] font-normal '>{__('Active Contract')}</Typography>
                <Chip
                  label={__(contractStatus as string)}
                  className='h-[24px] rounded-full'
                  sx={{
                    backgroundColor: getBackgroundColor(contractStatus as string),
                    color: getStatusColor(contractStatus as string)
                  }}
                />
              </div>
              <div className='flex gap-2 items-center'>
                <Typography className='text-[15px] leading-[22px] font-normal '>
                  {dataContract.data.file.name || __('Contract File')}
                </Typography>
                <IconButton onClick={() => window.open(dataContract.data.file.url, '_blank')}>
                  <i className='tabler-eye' />
                </IconButton>
              </div>
              <div className='flex flex-row gap-2'>
                <Typography className='text-[15px] leading-[22px] font-normal '>
                  {__('Start')}: {startDate?.format('DD MMM YYYY')} | {__('End')}: {endDate?.format('DD MMM YYYY')}
                </Typography>
              </div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box className='flex flex-col gap-3'>
                <Box className='flex justify-between items-center'>
                  <Typography className='text-[15px] leading-[22px] font-normal text-[#2F2B3D]'>
                    {__('Days')}
                  </Typography>
                  <Typography className='text-[15px] leading-[22px] font-normal text-[#2F2B3D]'>
                    {passedDays} {__('of')} {totalDays} {__('Days')}
                  </Typography>
                </Box>

                <LinearProgress
                  variant='determinate'
                  value={progressPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getProgressBarColor(progressPercentage),
                      borderRadius: 4
                    }
                  }}
                />
                <Typography className='text-[15px] leading-[22px] font-normal text-[#2F2B3D99]'>
                  {remainingDays} {__('days remaining until your Active Contract requires update.')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <div className='flex flex-start gap-2'>
            <Button
              variant='contained'
              className='text-[#fff] bg-[#3551F2] shadow-none rounded-full'
              onClick={handleDownload}
            >
              {__('Download')}
            </Button>
          </div>
        </Box>
      ) : (
        <Box className='p-4 bg-[#F3F2F3] rounded-[16px] flex items-center justify-center'>
          <Typography className='text-[15px] leading-[22px] font-normal text-[#2F2B3D99] h-[180px] flex items-center justify-center'>
            {__('No active contract found. Please upload a contract.')}
          </Typography>
        </Box>
      )}
      <UploadContractDialog
        open={openUploadContractDialog}
        onClose={() => setOpenUploadContractDialog(false)}
        refetch={refetch}
      />
    </Box>
  )
}
